import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { AppShellLayout } from "./layouts/AppShellLayout.js";
import { AuthShellLayout } from "./layouts/AuthShellLayout.js";
// Pages du 1er rendu gardées en eager (pas de chunk async au démarrage) :
// racine publique, connexion, tableau de bord post-login, et le placeholder
// "À venir" (fallback de nombreuses routes + catch-all 404).
import { ComingSoonPage } from "./pages/ComingSoon.js";
import { DashboardPage } from "./pages/Dashboard.js";
import { HomePage } from "./pages/Home.js";
import { LoginPage } from "./pages/Login.js";

// Pages lazy-loadées : économise ~30-50 KB gzip sur le bundle initial,
// + isolent maplibre-gl (~800 KB) sur les routes parcellaire/carte.
// Écrans peu fréquents (parcours auth annexes, paramètres, profil) passés en
// lazy pour alléger le chunk d'entrée sans coût perçu (chargés à la demande).
const AucunDomainePage = lazy(() =>
  import("./pages/AucunDomaine.js").then((m) => ({ default: m.AucunDomainePage })),
);
const ChoisirDomainePage = lazy(() =>
  import("./pages/ChoisirDomaine.js").then((m) => ({ default: m.ChoisirDomainePage })),
);
const CompteEnAttentePage = lazy(() =>
  import("./pages/CompteEnAttente.js").then((m) => ({ default: m.CompteEnAttentePage })),
);
const ConfirmerEmailPage = lazy(() =>
  import("./pages/ConfirmerEmail.js").then((m) => ({ default: m.ConfirmerEmailPage })),
);
const ConfirmerEmailAttentePage = lazy(() =>
  import("./pages/ConfirmerEmailAttente.js").then((m) => ({
    default: m.ConfirmerEmailAttentePage,
  })),
);
const DomaineSettingsPage = lazy(() =>
  import("./pages/DomaineSettings.js").then((m) => ({ default: m.DomaineSettingsPage })),
);
const HandoffPage = lazy(() =>
  import("./pages/Handoff.js").then((m) => ({ default: m.HandoffPage })),
);
const InvitationPage = lazy(() =>
  import("./pages/Invitation.js").then((m) => ({ default: m.InvitationPage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/Onboarding.js").then((m) => ({ default: m.OnboardingPage })),
);
const ParametresIndexPage = lazy(() =>
  import("./pages/Parametres.js").then((m) => ({ default: m.ParametresIndexPage })),
);
const ParametresMembresPage = lazy(() =>
  import("./pages/ParametresMembres.js").then((m) => ({ default: m.ParametresMembresPage })),
);
const ParametresRolesPage = lazy(() =>
  import("./pages/ParametresRoles.js").then((m) => ({ default: m.ParametresRolesPage })),
);
const PingPage = lazy(() => import("./pages/Ping.js").then((m) => ({ default: m.PingPage })));
const ProfilePage = lazy(() =>
  import("./pages/Profile.js").then((m) => ({ default: m.ProfilePage })),
);
// Module Vignoble (Lot B.5). Passé en lazy : sa chaîne d'imports statiques
// (VignobleCarteView → MapLibreCarte → maplibre-gl ~800 KB) sortait tout
// maplibre du chunk d'entrée. En lazy, maplibre n'est plus chargé qu'à la
// visite de /vignoble (et la carte elle-même est encore différée, cf Vignoble.tsx).
const VignoblePage = lazy(() =>
  import("./pages/Vignoble.js").then((m) => ({ default: m.VignoblePage })),
);
const FicheParcellePage = lazy(() =>
  import("./pages/vignoble/FicheParcelle.js").then((m) => ({ default: m.FicheParcellePage })),
);
const SignupPage = lazy(() => import("./pages/Signup.js").then((m) => ({ default: m.SignupPage })));
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPassword.js").then((m) => ({ default: m.ResetPasswordPage })),
);
const MagicLinkPage = lazy(() =>
  import("./pages/MagicLink.js").then((m) => ({ default: m.MagicLinkPage })),
);
const ProfileMfaSetupPage = lazy(() =>
  import("./pages/ProfileMfaSetup.js").then((m) => ({ default: m.ProfileMfaSetupPage })),
);
const ProfilePasswordPage = lazy(() =>
  import("./pages/ProfilePassword.js").then((m) => ({ default: m.ProfilePasswordPage })),
);
const ProfileSessionsPage = lazy(() =>
  import("./pages/ProfileSessions.js").then((m) => ({ default: m.ProfileSessionsPage })),
);
const NouvelleParcellePage = lazy(() =>
  import("./pages/vignoble/NouvelleParcelle.js").then((m) => ({ default: m.NouvelleParcellePage })),
);
const ModifierParcellePage = lazy(() =>
  import("./pages/vignoble/ModifierParcelle.js").then((m) => ({ default: m.ModifierParcellePage })),
);
// Module Vendange (Lot C.1 — écrans terrain). Lazy-loadé : isole la couche
// hors-ligne (Dexie) sur les routes vendange uniquement.
const VendangePage = lazy(() =>
  import("./pages/vendange/Vendange.js").then((m) => ({ default: m.VendangePage })),
);
const NouvellePeseePage = lazy(() =>
  import("./pages/vendange/NouvellePesee.js").then((m) => ({ default: m.NouvellePeseePage })),
);
const FichePeseePage = lazy(() =>
  import("./pages/vendange/FichePesee.js").then((m) => ({ default: m.FichePeseePage })),
);
const BordereauxApportPage = lazy(() =>
  import("./pages/vendange/BordereauxApport.js").then((m) => ({ default: m.BordereauxApportPage })),
);
// Module Cuverie (Lot C — pressurage, vins clairs, réserve, parc de cuves).
// Lazy par section : chaque écran arrive à la demande, le hub reste léger.
const CuveriePage = lazy(() =>
  import("./pages/cuverie/Cuverie.js").then((m) => ({ default: m.CuveriePage })),
);
const IntrantsStockPage = lazy(() =>
  import("./pages/cuverie/intrants/IntrantsStockPage.js").then((m) => ({
    default: m.IntrantsStockPage,
  })),
);
const PreparationsTiragePage = lazy(() =>
  import("./pages/cuverie/intrants/PreparationsTiragePage.js").then((m) => ({
    default: m.PreparationsTiragePage,
  })),
);
const CuvesListPage = lazy(() =>
  import("./pages/cuverie/cuves/index.js").then((m) => ({ default: m.CuvesListPage })),
);
const NouvelleCuvePage = lazy(() =>
  import("./pages/cuverie/cuves/index.js").then((m) => ({ default: m.NouvelleCuvePage })),
);
const FicheCuvePage = lazy(() =>
  import("./pages/cuverie/cuves/index.js").then((m) => ({ default: m.FicheCuvePage })),
);
const ModifierCuvePage = lazy(() =>
  import("./pages/cuverie/cuves/index.js").then((m) => ({ default: m.ModifierCuvePage })),
);
const MarcsListPage = lazy(() =>
  import("./pages/cuverie/pressurage/index.js").then((m) => ({ default: m.MarcsListPage })),
);
const MarcFormPage = lazy(() =>
  import("./pages/cuverie/pressurage/index.js").then((m) => ({ default: m.MarcFormPage })),
);
const FicheMarcPage = lazy(() =>
  import("./pages/cuverie/pressurage/index.js").then((m) => ({ default: m.FicheMarcPage })),
);
const EnregistrerFractionsPage = lazy(() =>
  import("./pages/cuverie/pressurage/index.js").then((m) => ({
    default: m.EnregistrerFractionsPage,
  })),
);
const VinsClairsListPage = lazy(() =>
  import("./pages/cuverie/vins-clairs/index.js").then((m) => ({ default: m.VinsClairsListPage })),
);
const NouveauVinClairPage = lazy(() =>
  import("./pages/cuverie/vins-clairs/index.js").then((m) => ({ default: m.NouveauVinClairPage })),
);
const FicheVinClairPage = lazy(() =>
  import("./pages/cuverie/vins-clairs/index.js").then((m) => ({ default: m.FicheVinClairPage })),
);
const ReserveBilanPage = lazy(() =>
  import("./pages/cuverie/reserve/index.js").then((m) => ({ default: m.ReserveBilanPage })),
);
// Chantier Lot I — suivi des fermentations (tournée densité/température,
// jalons FA/FML) et registre des chaptalisations (enrichissement + OENO).
const FermentationsPage = lazy(() =>
  import("./pages/cuverie/fermentations/index.js").then((m) => ({ default: m.FermentationsPage })),
);
const ChaptalisationsRegistrePage = lazy(() =>
  import("./pages/cuverie/chaptalisations/index.js").then((m) => ({
    default: m.ChaptalisationsRegistrePage,
  })),
);
const ChaptalisationFormPage = lazy(() =>
  import("./pages/cuverie/chaptalisations/index.js").then((m) => ({
    default: m.ChaptalisationFormPage,
  })),
);
// Module Cave (Lot D — cuvées → assemblage → tirage → sur lattes → dégorgement
// → habillage + plan 2D). Lazy PAR SECTION (un barrel index.ts par dossier) :
// chaque section arrive à la demande, le hub reste léger. CONVENTION
// CONTRACTUELLE : les groupes front suivants remplacent le CONTENU des pages
// placeholder en gardant fichiers + noms d'export — ces imports ne bougent pas.
const CaveHubPage = lazy(() =>
  import("./pages/cave/CaveHub.js").then((m) => ({ default: m.CaveHubPage })),
);
const CuveesListPage = lazy(() =>
  import("./pages/cave/cuvees/index.js").then((m) => ({ default: m.CuveesListPage })),
);
const NouvelleCuveePage = lazy(() =>
  import("./pages/cave/cuvees/index.js").then((m) => ({ default: m.NouvelleCuveePage })),
);
const CuveeDetailPage = lazy(() =>
  import("./pages/cave/cuvees/index.js").then((m) => ({ default: m.CuveeDetailPage })),
);
const ModifierCuveePage = lazy(() =>
  import("./pages/cave/cuvees/index.js").then((m) => ({ default: m.ModifierCuveePage })),
);
const AssemblagesListPage = lazy(() =>
  import("./pages/cave/assemblages/index.js").then((m) => ({ default: m.AssemblagesListPage })),
);
const AssemblageAtelierPage = lazy(() =>
  import("./pages/cave/assemblages/index.js").then((m) => ({ default: m.AssemblageAtelierPage })),
);
const AssemblageDetailPage = lazy(() =>
  import("./pages/cave/assemblages/index.js").then((m) => ({ default: m.AssemblageDetailPage })),
);
const TiragesListPage = lazy(() =>
  import("./pages/cave/tirages/index.js").then((m) => ({ default: m.TiragesListPage })),
);
const TirageFormPage = lazy(() =>
  import("./pages/cave/tirages/index.js").then((m) => ({ default: m.TirageFormPage })),
);
const TirageDetailPage = lazy(() =>
  import("./pages/cave/tirages/index.js").then((m) => ({ default: m.TirageDetailPage })),
);
// Lot I — déclarations de tirage (intention 48 h + revendication AIDAC).
const DeclarationsTiragePage = lazy(() =>
  import("./pages/cave/tirages/index.js").then((m) => ({ default: m.DeclarationsTiragePage })),
);
const RepriseFormPage = lazy(() =>
  import("./pages/cave/reprises/RepriseForm.js").then((m) => ({ default: m.RepriseFormPage })),
);
const LotDetailPage = lazy(() =>
  import("./pages/cave/stock/index.js").then((m) => ({ default: m.LotDetailPage })),
);
// Module Stocks bouteilles (Lot E.2) — vues agrégées + inventaire + seuils.
// Promu en entrée sidebar 1er niveau `/stock` (canonique) ; `/cave/stock*` redirige.
const StockVuesPage = lazy(() =>
  import("./pages/stock/index.js").then((m) => ({ default: m.StockVuesPage })),
);
const InventairePage = lazy(() =>
  import("./pages/stock/index.js").then((m) => ({ default: m.InventairePage })),
);
const SeuilsStockPage = lazy(() =>
  import("./pages/stock/index.js").then((m) => ({ default: m.SeuilsStockPage })),
);
// Lot I — inventaire en session (comptage → écarts → clôture append-only).
const InventairesSessionsPage = lazy(() =>
  import("./pages/stock/inventaires/index.js").then((m) => ({ default: m.InventairesPage })),
);
const SessionInventairePage = lazy(() =>
  import("./pages/stock/inventaires/index.js").then((m) => ({ default: m.SessionInventairePage })),
);
// Lot I — stock cols / matières sèches (remplace le ComingSoon /stock-cols).
const StockColsPage = lazy(() =>
  import("./pages/stock-cols/index.js").then((m) => ({ default: m.StockColsPage })),
);
// Module Étiquetage (Lot E.1) — liste des lots + fiche de mentions AOC.
const EtiquetageListPage = lazy(() =>
  import("./pages/etiquetage/index.js").then((m) => ({ default: m.EtiquetageListPage })),
);
const FicheMentionsPage = lazy(() =>
  import("./pages/etiquetage/index.js").then((m) => ({ default: m.FicheMentionsPage })),
);
// Module Conformité (Lot F) — registre de cave, DRM, DR, DS, CRD, expédition.
const HubConformitePage = lazy(() =>
  import("./pages/conformite/HubConformite.js").then((m) => ({ default: m.HubConformitePage })),
);
const RegistreCavePage = lazy(() =>
  import("./pages/conformite/RegistreCave.js").then((m) => ({ default: m.RegistreCavePage })),
);
const DrmAssistantPage = lazy(() =>
  import("./pages/conformite/drm/DrmAssistantPage.js").then((m) => ({
    default: m.DrmAssistantPage,
  })),
);
const DsAssistantPage = lazy(() =>
  import("./pages/conformite/ds/DsAssistantPage.js").then((m) => ({ default: m.DsAssistantPage })),
);
const DrAssistantPage = lazy(() =>
  import("./pages/conformite/dr/DrAssistantPage.js").then((m) => ({ default: m.DrAssistantPage })),
);
const CapsulesPage = lazy(() =>
  import("./pages/conformite/capsules/CapsulesPage.js").then((m) => ({ default: m.CapsulesPage })),
);
const BvdPage = lazy(() =>
  import("./pages/conformite/BvdPage.js").then((m) => ({ default: m.BvdPage })),
);
const ExpeditionPage = lazy(() =>
  import("./pages/conformite/expedition/ExpeditionPage.js").then((m) => ({
    default: m.ExpeditionPage,
  })),
);
// Module Ventes + Facturation (Lot G) — hub, catalogue, clients, documents
// (devis→commande→BL→facture→avoir), fiche facture, paramètres, vente caveau.
const HubVentesPage = lazy(() =>
  import("./pages/ventes/HubVentes.js").then((m) => ({ default: m.HubVentesPage })),
);
const CatalogueProduitsPage = lazy(() =>
  import("./pages/ventes/produits/index.js").then((m) => ({ default: m.CatalogueProduitsPage })),
);
const CarnetClientsPage = lazy(() =>
  import("./pages/ventes/clients/index.js").then((m) => ({ default: m.CarnetClientsPage })),
);
const NouveauClientPage = lazy(() =>
  import("./pages/ventes/clients/index.js").then((m) => ({ default: m.NouveauClientPage })),
);
const FicheClientPage = lazy(() =>
  import("./pages/ventes/clients/index.js").then((m) => ({ default: m.FicheClientPage })),
);
const ListeDocumentsPage = lazy(() =>
  import("./pages/ventes/documents/ListeDocuments.js").then((m) => ({
    default: m.ListeDocumentsPage,
  })),
);
const AssistantVenteB2BPage = lazy(() =>
  import("./pages/ventes/documents/AssistantVenteB2B.js").then((m) => ({
    default: m.AssistantVenteB2BPage,
  })),
);
const FicheFacturePage = lazy(() =>
  import("./pages/ventes/documents/FicheFacture.js").then((m) => ({ default: m.FicheFacturePage })),
);
const ParametresFacturationPage = lazy(() =>
  import("./pages/ventes/parametres/ParametresFacturation.js").then((m) => ({
    default: m.ParametresFacturationPage,
  })),
);
const VenteCaveauPage = lazy(() =>
  import("./pages/ventes/caveau/index.js").then((m) => ({ default: m.VenteCaveauPage })),
);
// FE-5 — relevé de ventes (ce qui ne passe pas par une facture électronique).
const ReleveVentesPage = lazy(() =>
  import("./pages/conformite/releve-ventes/index.js").then((m) => ({
    default: m.ReleveVentesPage,
  })),
);
// FE-2 — diagnostic « Prêt pour la facture électronique » (échéance 2027).
const PretPourFactureElectroniquePage = lazy(() =>
  import("./pages/ventes/facture-electronique/index.js").then((m) => ({
    default: m.PretPourFactureElectroniquePage,
  })),
);
// FE-1 — carnet des factures REÇUES (jalon réception du 01/09/2026).
const FacturesRecuesPage = lazy(() =>
  import("./pages/ventes/factures-recues/index.js").then((m) => ({
    default: m.FacturesRecuesPage,
  })),
);
const ImporterDocumentRecuPage = lazy(() =>
  import("./pages/ventes/factures-recues/index.js").then((m) => ({
    default: m.ImporterDocumentRecuPage,
  })),
);
const DocumentRecuDetailPage = lazy(() =>
  import("./pages/ventes/factures-recues/index.js").then((m) => ({
    default: m.DocumentRecuDetailPage,
  })),
);
const VerifierPropositionDocumentPage = lazy(() =>
  import("./pages/ventes/factures-recues/index.js").then((m) => ({
    default: m.VerifierPropositionDocumentPage,
  })),
);
const DegorgementFormPage = lazy(() =>
  import("./pages/cave/degorgements/DegorgementForm.js").then((m) => ({
    default: m.DegorgementFormPage,
  })),
);
const HabillageFormPage = lazy(() =>
  import("./pages/cave/habillage/HabillageForm.js").then((m) => ({
    default: m.HabillageFormPage,
  })),
);
const PlanCavePage = lazy(() =>
  import("./pages/cave/plan/PlanCave.js").then((m) => ({ default: m.PlanCavePage })),
);
// Module Travaux + Phyto & IFT (Lot 2). Lazy : isole le module de saisie phyto
// (autocomplete catalogue, IFT live) du bundle initial des autres écrans.
const CarnetPage = lazy(() =>
  import("./pages/vignoble/travaux/CarnetPage.js").then((m) => ({ default: m.CarnetPage })),
);
const GroupesPage = lazy(() =>
  import("./pages/vignoble/groupes/GroupesPage.js").then((m) => ({ default: m.GroupesPage })),
);
const GroupeFormPage = lazy(() =>
  import("./pages/vignoble/groupes/GroupeFormPage.js").then((m) => ({ default: m.GroupeFormPage })),
);
const NouvelleInterventionPage = lazy(() =>
  import("./pages/vignoble/passages/NouvelleInterventionPage.js").then((m) => ({
    default: m.NouvelleInterventionPage,
  })),
);
const PassagesPage = lazy(() =>
  import("./pages/vignoble/passages/PassagesPage.js").then((m) => ({ default: m.PassagesPage })),
);
const PassageDetailPage = lazy(() =>
  import("./pages/vignoble/passages/PassageDetailPage.js").then((m) => ({
    default: m.PassageDetailPage,
  })),
);
const PhytoPage = lazy(() =>
  import("./pages/vignoble/phyto/PhytoPage.js").then((m) => ({ default: m.PhytoPage })),
);
const CatalogueAgricolePage = lazy(() =>
  import("./pages/vignoble/catalogue-agricole/CatalogueAgricolePage.js").then((m) => ({
    default: m.CatalogueAgricolePage,
  })),
);
const ArticleAgricoleDetailPage = lazy(() =>
  import("./pages/vignoble/catalogue-agricole/ArticleAgricoleDetailPage.js").then((m) => ({
    default: m.ArticleAgricoleDetailPage,
  })),
);
// Module Parc matériel + maintenance + alertes (Lot 3). Lazy : 4ᵉ onglet
// Vignoble (liste / création / fiche), isolé du bundle initial.
const MaterielListPage = lazy(() =>
  import("./pages/vignoble/materiel/MaterielListPage.js").then((m) => ({
    default: m.MaterielListPage,
  })),
);
const MaterielCreatePage = lazy(() =>
  import("./pages/vignoble/materiel/MaterielCreatePage.js").then((m) => ({
    default: m.MaterielCreatePage,
  })),
);
const MaterielFichePage = lazy(() =>
  import("./pages/vignoble/materiel/MaterielFichePage.js").then((m) => ({
    default: m.MaterielFichePage,
  })),
);
// Module Aide (Lot H.4) — centre d'aide (index + article markdown). Lazy :
// isole le rendu markdown maison du bundle initial.
const AidePage = lazy(() =>
  import("./pages/aide/AidePage.js").then((m) => ({ default: m.AidePage })),
);
const ArticleAidePage = lazy(() =>
  import("./pages/aide/ArticleAide.js").then((m) => ({ default: m.ArticleAidePage })),
);
// Module Webshop — BOUTIQUE PUBLIQUE (Phase 6a). Section `/boutique/*` montée HORS
// du guard d'auth ERP, avec son PROPRE design system (import de boutique.css isolé) et
// sa bascule de template. Lazy-loadée : la boutique (thèmes + écrans B2C) n'alourdit pas
// le bundle initial de l'ERP, et l'ERP n'alourdit pas la boutique.
const BoutiqueApp = lazy(() =>
  import("./boutique/BoutiqueApp.js").then((m) => ({ default: m.BoutiqueApp })),
);
// Module Webshop — BACKOFFICE vigneron (Phase 6b). Écrans DANS l'ERP authentifié
// (charte ERP) : hub de config + sections (apparence/livraison/paiement/mentions),
// publication produits + INCO, gestion des commandes (préparer/expédier/facturer).
// Lazy par section via le barrel `boutique-admin/index.ts` (un seul chunk demandé).
const BoutiqueHubPage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.BoutiqueHubPage })),
);
const ConfigApparencePage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.ConfigApparencePage })),
);
const ConfigLivraisonPage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.ConfigLivraisonPage })),
);
const ConfigPaiementPage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.ConfigPaiementPage })),
);
const ConfigMentionsPage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.ConfigMentionsPage })),
);
const ProduitsPubliesPage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.ProduitsPubliesPage })),
);
const CommandesWebListePage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.CommandesListePage })),
);
const FicheCommandeWebPage = lazy(() =>
  import("./pages/boutique-admin/index.js").then((m) => ({ default: m.FicheCommandePage })),
);

/** Redirige `/cave/stock/:id` → `/stock/:id` en préservant l'identifiant. */
function RedirectToStockLot() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/stock/${id}`} replace />;
}

export function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* Routes auth groupées sous AuthShellLayout : ce parent (qui porte
          le BubblesCanvas) reste monté tant qu'on navigue à l'intérieur du
          groupe → l'animation des bulles ne se réinitialise pas entre
          /login, /signup, /reset-password, /magic-link et /invitation. */}
        <Route element={<AuthShellLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/magic-link" element={<MagicLinkPage />} />
          <Route path="/invitation/:token" element={<InvitationPage />} />
          {/* Page sas pour user authentifié sans aucune membership active.
            Dans le AuthShellLayout (et non AppShell) parce que l'utilisateur
            n'a justement PAS de domaine — la sidebar/topbar du shell
            principal n'aurait rien de cohérent à afficher. */}
          <Route path="/aucun-domaine" element={<AucunDomainePage />} />
          {/* Page sas pour user dont tous les domaines accessibles sont en
            `en_attente_validation` (signup en mode modération, B.4.12).
            Sous AuthShell pour la même raison qu'AucunDomaine. */}
          <Route path="/compte-en-attente" element={<CompteEnAttentePage />} />
          {/* Feature email-verification : page d'attente affichée tant que
            `email_verifie_le IS NULL` sur le compte connecté. La garde
            front (AppShellLayout) y redirige automatiquement, et le backend
            (emailVerifiedGuardMiddleware) bloque toutes les routes app. */}
          <Route path="/confirmer-email-attente" element={<ConfirmerEmailAttentePage />} />
          {/* Feature email-verification : page de confirmation accédée via
            le lien du mail. Publique (token = secret, pas besoin de session
            préalable). POST volontaire au clic du bouton, cf threat model. */}
          <Route path="/confirmer-email" element={<ConfirmerEmailPage />} />
          {/* Page sas pour user multi-domaines (2+ memberships actives) qui
            vient de se connecter et n'a pas encore choisi son tenant actif.
            La sélection ici déclenche `auth.switchTenant` qui pose le tenant
            ET marque la `derniere_connexion_le` côté serveur. */}
          <Route path="/choisir-domaine" element={<ChoisirDomainePage />} />
          {/* Page handoff : consume du token éphémère après login multi-tenant.
            Servie sur le sous-domaine tenant (`<slug>.crayea.fr`). */}
          <Route path="/auth/handoff" element={<HandoffPage />} />
        </Route>
        <Route path="/ping" element={<PingPage />} />
        {/* Boutique publique (Webshop B2C, Phase 6a) — ANONYME, hors AppShellLayout
          (pas de RequireAuth). Design system propre, tenant résolu serveur depuis le
          slug du Host. `/*` : la boutique gère son propre routeur interne. */}
        <Route path="/boutique/*" element={<BoutiqueApp />} />
        {/* Routes ERP authentifiées groupées sous AppShellLayout : porte
          <RequireAuth> + Sidebar + Topbar + BubblesCanvas. Persistant entre
          /dashboard, /parametres/* et futurs modules → les bulles ne
          se réinitialisent plus à chaque navigation interne. */}
        <Route element={<AppShellLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/parametres" element={<ParametresIndexPage />} />
          <Route path="/parametres/domaine" element={<DomaineSettingsPage />} />
          <Route path="/parametres/membres" element={<ParametresMembresPage />} />
          <Route path="/parametres/roles" element={<ParametresRolesPage />} />
          {/* Module Vignoble (Lot B.5 — Parcellaire). Liste + Carte + CRUD parcelles.
            Ordre des routes : les routes statiques (`/carte`, `/nouvelle`) avant
            les routes paramétrées (`/parcelle/:id`) pour que React Router matche
            le plus spécifique d'abord. */}
          <Route path="/vignoble" element={<VignoblePage />} />
          <Route path="/vignoble/nouvelle" element={<NouvelleParcellePage />} />
          {/* Onglets Travaux + Phyto & IFT (Lot 2) — même entrée sidebar
            « Vignoble », onglet actif dérivé de l'URL (VignobleTabs). */}
          <Route path="/vignoble/travaux" element={<CarnetPage />} />
          <Route path="/vignoble/travaux/groupes" element={<GroupesPage />} />
          <Route path="/vignoble/travaux/groupes/nouveau" element={<GroupeFormPage />} />
          <Route path="/vignoble/travaux/groupes/:groupeId" element={<GroupeFormPage />} />
          <Route path="/vignoble/travaux/nouvelle" element={<NouvelleInterventionPage />} />
          <Route path="/vignoble/travaux/passages" element={<PassagesPage />} />
          <Route path="/vignoble/travaux/passages/:passageId" element={<PassageDetailPage />} />
          <Route path="/vignoble/phyto" element={<PhytoPage />} />
          <Route path="/vignoble/catalogue" element={<CatalogueAgricolePage />} />
          <Route
            path="/vignoble/catalogue/articles/:articleId"
            element={<ArticleAgricoleDetailPage />}
          />
          {/* Onglet Matériel (Lot 3) — parc matériel + maintenance + alertes.
            Route statique `/nouveau` AVANT la route paramétrée `/:id` pour que
            React Router matche le plus spécifique d'abord. */}
          <Route path="/vignoble/materiel" element={<MaterielListPage />} />
          <Route path="/vignoble/materiel/nouveau" element={<MaterielCreatePage />} />
          <Route path="/vignoble/materiel/:id" element={<MaterielFichePage />} />
          <Route path="/vignoble/parcelle/:id" element={<FicheParcellePage />} />
          <Route path="/vignoble/parcelle/:id/modifier" element={<ModifierParcellePage />} />
          {/* Module Vendange (Lot C.1 — écrans terrain). Routes statiques
            (`/pesee/nouvelle`, `/bordereaux`) avant la route paramétrée
            (`/pesee/:id`) pour que React Router matche le plus spécifique
            d'abord. */}
          <Route path="/vendange" element={<VendangePage />} />
          <Route path="/vendange/pesee/nouvelle" element={<NouvellePeseePage />} />
          <Route path="/vendange/pesee/:id" element={<FichePeseePage />} />
          <Route path="/vendange/bordereaux" element={<BordereauxApportPage />} />
          {/* Module Cuverie (Lot C). Le hub `/cuverie` aiguille vers les quatre
            sections. Routes statiques (`/plan`, `/nouvelle`, `/nouveau`,
            `/fractions`) AVANT les routes paramétrées (`/:id`) pour que React
            Router matche le plus spécifique d'abord. */}
          <Route path="/cuverie" element={<CuveriePage />} />
          <Route path="/cuverie/intrants-stock" element={<IntrantsStockPage />} />
          <Route path="/cuverie/preparations-tirage" element={<PreparationsTiragePage />} />
          {/* Lot I É1 — suivi des fermentations (tournée + grille FA/FML). */}
          <Route path="/cuverie/fermentations" element={<FermentationsPage />} />
          <Route path="/cuverie/cuves" element={<CuvesListPage />} />
          {/* Le plan 2D a rejoint le module Cave (une seule vérité spatiale,
            décision UX Lot D) — l'ancienne URL reste vivante par redirection. */}
          <Route path="/cuverie/cuves/plan" element={<Navigate to="/cave/plan" replace />} />
          <Route path="/cuverie/cuves/nouvelle" element={<NouvelleCuvePage />} />
          <Route path="/cuverie/cuves/:id" element={<FicheCuvePage />} />
          <Route path="/cuverie/cuves/:id/modifier" element={<ModifierCuvePage />} />
          <Route path="/cuverie/pressurage" element={<MarcsListPage />} />
          <Route path="/cuverie/pressurage/nouveau" element={<MarcFormPage />} />
          <Route path="/cuverie/pressurage/:id" element={<FicheMarcPage />} />
          <Route path="/cuverie/pressurage/:id/fractions" element={<EnregistrerFractionsPage />} />
          <Route path="/cuverie/vins-clairs" element={<VinsClairsListPage />} />
          <Route path="/cuverie/vins-clairs/nouveau" element={<NouveauVinClairPage />} />
          <Route path="/cuverie/vins-clairs/:id" element={<FicheVinClairPage />} />
          <Route path="/cuverie/reserve" element={<ReserveBilanPage />} />
          {/* Lot I É3 — chaptalisations : route statique `/nouvelle` après
            le registre (aucune route paramétrée dans cette section). */}
          <Route path="/cuverie/chaptalisations" element={<ChaptalisationsRegistrePage />} />
          <Route path="/cuverie/chaptalisations/nouvelle" element={<ChaptalisationFormPage />} />
          {/* Module Cave (Lot D). Le hub `/cave` aiguille vers les six étapes
            du flux. Routes statiques (`/nouvelle`, `/nouveau`) AVANT les
            routes paramétrées (`/:id`) pour que React Router matche le plus
            spécifique d'abord. */}
          <Route path="/cave" element={<CaveHubPage />} />
          <Route path="/cave/cuvees" element={<CuveesListPage />} />
          <Route path="/cave/cuvees/nouvelle" element={<NouvelleCuveePage />} />
          <Route path="/cave/cuvees/:id" element={<CuveeDetailPage />} />
          <Route path="/cave/cuvees/:id/modifier" element={<ModifierCuveePage />} />
          <Route path="/cave/assemblages" element={<AssemblagesListPage />} />
          <Route path="/cave/assemblages/nouveau" element={<AssemblageAtelierPage />} />
          <Route path="/cave/assemblages/:id" element={<AssemblageDetailPage />} />
          <Route path="/cave/tirages" element={<TiragesListPage />} />
          <Route path="/cave/tirages/nouveau" element={<TirageFormPage />} />
          {/* Lot I — déclarations de tirage : route statique AVANT `/:id`. */}
          <Route path="/cave/tirages/declarations" element={<DeclarationsTiragePage />} />
          <Route path="/cave/tirages/:id" element={<TirageDetailPage />} />
          <Route path="/cave/reprises/nouvelle" element={<RepriseFormPage />} />
          <Route path="/cave/degorgements/nouveau" element={<DegorgementFormPage />} />
          <Route path="/cave/habillage/nouveau" element={<HabillageFormPage />} />
          <Route path="/cave/plan" element={<PlanCavePage />} />
          {/* Ancienne entrée sidebar « Tirage / Dégorgement » → hub Cave. */}
          <Route path="/tirage-degorgement/*" element={<Navigate to="/cave" replace />} />
          {/* Module Stocks bouteilles (Lot E.2). Canonique `/stock` — routes
            statiques (`/inventaire`, `/seuils`) AVANT la route paramétrée
            (`/:id`) pour que React Router matche le plus spécifique d'abord.
            La fiche lot reste servie par `LotDetailPage` (module cave/stock). */}
          <Route path="/stock" element={<StockVuesPage />} />
          <Route path="/stock/inventaire" element={<InventairePage />} />
          {/* Lot I É5 — inventaire en session (routes statiques avant `/:id`). */}
          <Route path="/stock/inventaires" element={<InventairesSessionsPage />} />
          <Route path="/stock/inventaires/:id" element={<SessionInventairePage />} />
          <Route path="/stock/seuils" element={<SeuilsStockPage />} />
          <Route path="/stock/:id" element={<LotDetailPage />} />
          {/* Redirections /cave/stock* → /stock* : une seule vérité du stock. */}
          <Route path="/cave/stock" element={<Navigate to="/stock" replace />} />
          <Route path="/cave/stock/:id" element={<RedirectToStockLot />} />
          {/* Module Étiquetage (Lot E.1) — mentions AOC. Route statique liste
            AVANT la route paramétrée fiche (`/:lotId`). */}
          <Route path="/etiquetage" element={<EtiquetageListPage />} />
          <Route path="/etiquetage/:lotId" element={<FicheMentionsPage />} />
          {/* Lot I É4 — stock cols / matières sèches (vraie page, ex-ComingSoon). */}
          <Route path="/stock-cols" element={<StockColsPage />} />
          {/* Module Ventes + Facturation (Lot G). Routes statiques AVANT les
              dynamiques (:id) pour ne pas capturer "nouveau"/"catalogue". */}
          <Route path="/ventes" element={<HubVentesPage />} />
          <Route path="/ventes/catalogue" element={<CatalogueProduitsPage />} />
          <Route path="/ventes/clients" element={<CarnetClientsPage />} />
          <Route path="/ventes/clients/nouveau" element={<NouveauClientPage />} />
          <Route path="/ventes/clients/:id" element={<FicheClientPage />} />
          <Route path="/ventes/documents" element={<ListeDocumentsPage />} />
          <Route path="/ventes/documents/nouveau" element={<AssistantVenteB2BPage />} />
          <Route path="/ventes/documents/:id" element={<AssistantVenteB2BPage />} />
          <Route path="/ventes/factures/:id" element={<FicheFacturePage />} />
          <Route path="/ventes/factures-recues/importer" element={<ImporterDocumentRecuPage />} />
          <Route
            path="/ventes/factures-recues/documents/:documentId"
            element={<DocumentRecuDetailPage />}
          />
          <Route
            path="/ventes/factures-recues/propositions/:propositionId/verifier"
            element={<VerifierPropositionDocumentPage />}
          />
          <Route path="/ventes/factures-recues" element={<FacturesRecuesPage />} />
          <Route
            path="/ventes/facture-electronique"
            element={<PretPourFactureElectroniquePage />}
          />
          <Route path="/conformite/releve-ventes" element={<ReleveVentesPage />} />
          <Route path="/ventes/parametres" element={<ParametresFacturationPage />} />
          <Route path="/ventes/caveau" element={<VenteCaveauPage />} />
          {/* Module Webshop — BACKOFFICE (Phase 6b). Config boutique + sections,
              produits publiés, commandes web. Routes statiques AVANT la route
              paramétrée `/commandes/:id`. Distinct de `/boutique/*` (vitrine
              publique, hors AppShell). */}
          <Route path="/boutique-admin" element={<BoutiqueHubPage />} />
          <Route path="/boutique-admin/apparence" element={<ConfigApparencePage />} />
          <Route path="/boutique-admin/livraison" element={<ConfigLivraisonPage />} />
          <Route path="/boutique-admin/paiement" element={<ConfigPaiementPage />} />
          <Route path="/boutique-admin/mentions" element={<ConfigMentionsPage />} />
          <Route path="/boutique-admin/produits" element={<ProduitsPubliesPage />} />
          <Route path="/boutique-admin/commandes" element={<CommandesWebListePage />} />
          <Route path="/boutique-admin/commandes/:id" element={<FicheCommandeWebPage />} />
          <Route path="/expedition/*" element={<Navigate to="/conformite/expedition" replace />} />
          {/* Modules métier non encore implémentés : placeholder "À venir" sous
            AppShellLayout pour conserver sidebar + topbar. */}
          <Route path="/equipes/*" element={<ComingSoonPage />} />
          {/* Module Conformité (Lot F) — registre de cave + déclarations DRM/DR/DS
            + suivi CRD + sortie/expédition. Routes statiques (pas de wildcard). */}
          <Route path="/conformite" element={<HubConformitePage />} />
          <Route path="/conformite/registre" element={<RegistreCavePage />} />
          <Route path="/conformite/drm" element={<DrmAssistantPage />} />
          <Route path="/conformite/dr" element={<DrAssistantPage />} />
          <Route path="/conformite/ds" element={<DsAssistantPage />} />
          <Route path="/conformite/capsules" element={<CapsulesPage />} />
          <Route path="/conformite/bvd" element={<BvdPage />} />
          <Route path="/conformite/expedition" element={<ExpeditionPage />} />
          <Route path="/statistiques/*" element={<ComingSoonPage />} />
          {/* Module Aide (Lot H.4) — centre d'aide + articles markdown. Route
            statique `/aide` AVANT la route paramétrée `/aide/:slug`. */}
          <Route path="/aide" element={<AidePage />} />
          <Route path="/aide/:slug" element={<ArticleAidePage />} />
          <Route path="/parametres/abonnement" element={<ComingSoonPage />} />
          {/* Wizard d'onboarding (Lot B.4) — accessible quand le domaine est
            actif mais que l'onboarding n'est pas terminé. Sous AppShell pour
            que le polling `useMe` reste actif et que la garde « domaine en
            attente » bascule l'écran vers /compte-en-attente si nécessaire. */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          {/* Page index « Mon compte » — regroupe toutes les sous-pages liées
            à l'utilisateur (sécurité, sessions, à venir : infos perso,
            préférences, notifications). */}
          <Route path="/profile" element={<ProfilePage />} />
          {/* Mes sessions actives — Lot B.2.7. Accessible aussi en mode hub
            (le user voit ses sessions sur tous tenants confondus). */}
          <Route path="/profile/sessions" element={<ProfileSessionsPage />} />
          <Route path="/profile/password" element={<ProfilePasswordPage />} />
          {/* Activation MFA TOTP — Lot B.2.6. Obligatoire avant connexion si
            user multi-tenant ; sinon optionnel via menu utilisateur. */}
          <Route path="/profile/mfa/setup" element={<ProfileMfaSetupPage />} />
          {/* Catch-all dans le shell authentifié pour les vrais 404 */}
          <Route path="*" element={<ComingSoonPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
